import React, { useEffect, useState } from 'react';
import { FaHome } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import defaultInstance from '../../axiosHelper';
import { API_ENDPOINTS } from '../../utils/apiEndPoints';

import { Pie, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ScreeningFilter from './ScreeningFilter';
import { healthFacilitiesUpdate1 } from '../../utils/helper';
import moment from 'moment';
import { distWiseData } from './dummyData';
import CustomHeader from '../header';
import DateFilterModal from './DateFilterModal';
import ViewToggle from './ViewToggle';

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const headers = [
    { label: 'Total Health Facility where CMHA screening was taken up', key: 'health_facility_count' },
    { label: 'Total number of villages where annual CMHA screening was taken up', key: 'village_count' },
    { label: 'Total population screened', key: 'total_population' },
    { label: 'Male', key: 'male_population' },
    { label: 'Female', key: 'female_population' },
];

const summaryData = [
    { disease: 'DM', key: 'dm_cases' },
    { disease: 'HTN', key: 'htn_cases' },
    { disease: 'Mental Health', key: 'mh_cases' },
    { disease: 'Oral Cancer', key: 'oc_cases' },
    { disease: 'Breast Cancer', key: 'bc_cases' },
    { disease: 'Cervical Cancer', key: 'cc_cases' },
    { disease: 'CVD', key: 'cvd_cases' },
    { disease: 'COPD', key: 'copd_cases' },
    { disease: 'CKD', key: 'ckd_cases' },
    { disease: 'DM and HTN', key: 'dm_htn_cases' },
    { disease: 'Stroke/Post Stroke', key: 'ps_cases' },
    { disease: 'BMI > 25', key: 'bmi_cases' }
];

const StateWiseScreening = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const [screeningDetails, setScreeningDetails] = useState(null);
    const [healthFacilityCount, setHealthFacilityCount] = useState(0);
    const [villageCount, setVillageCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [filterData, setFilterData] = useState(null)
    const [showFilter, setShowFilter] = useState(false)
    const [viewType, setViewType] = useState('list')

    const headingName = state?.action_type == "district" ? state?.data : 'Manipur'

    const getFilteredFacilities = (facilities, filter) => {
        let filteredFacilities = [...facilities];

        // If date range is provided — filter by yes_month within that range
        if (filter?.from_date && filter?.to_date) {
            const fromDate = moment(filter.from_date, "YYYY-MM-DD");
            const toDate = moment(filter.to_date, "YYYY-MM-DD");

            // Generate array of month strings like "May-25", "Jun-25"
            const targetMonths = [];
            const tempDate = fromDate.clone().startOf("month");

            while (tempDate.isSameOrBefore(toDate, "month")) {
                targetMonths.push(tempDate.format("MMM-YY"));
                tempDate.add(1, "month");
            }

            filteredFacilities = filteredFacilities.filter(facility =>
                facility.yes_month.some(month => targetMonths.includes(month))
            );
        }

        // If district filter is provided — filter by district
        if (state?.action_type == "district" && state?.data) {
            filteredFacilities = filteredFacilities.filter(
                facility => facility?.district?.toLowerCase() === state?.data?.toLowerCase()
            );
        }

        return filteredFacilities;
    }


    const getVillageCount = () => {
        if (state?.action_type == "district" && state?.data) {
            const districtData = distWiseData()?.[state?.data]?.village_count
            return districtData || 0
        }
        return 1396
    }

    const getHFCount = () => {
        const result = getFilteredFacilities(healthFacilitiesUpdate1, filterData);
        return result?.length || 0;
    };

    const fetchData = async (params = {}) => {
        setIsLoading(true);
        const payload = { ...params }
        if (state?.action_type == "district") {
            payload.district = state?.data
        }
        const url = state?.action_type == "district" ? API_ENDPOINTS.GET_TEST_DASHBOARD_BY_DISTRICT : API_ENDPOINTS.GET_TEST_DASHBOARD
        const response = await defaultInstance.get(url, { params: payload });
        setIsLoading(false)
        if (response?.status === 200) {
            setScreeningDetails(response?.data);
            const hFCount = getHFCount();
            setHealthFacilityCount(hFCount);
            const villageCount = getVillageCount();
            setVillageCount(villageCount);
        }
    }

    useEffect(() => {
        fetchData({});
    }, [])

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'bottom',
            },
            datalabels: {
                color: '#fff',
                formatter: (value, context) => {
                    const data = context.chart.data.datasets[0].data;
                    const total = data.reduce((acc, val) => acc + val, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                    return `${percentage}%`;
                },
            },
        },
    };

    const genderData = {
        labels: ['Male', 'Female'],
        datasets: [
            {
                label: 'Population',
                data: [screeningDetails?.male_population, screeningDetails?.female_population],
                backgroundColor: ['#36A2EB', '#FF6384'],
                borderWidth: 1,
            },
        ],
    };


    const ageGroupsData = {
        labels: ['18-29 Years', '30-39 Years', '40-49 Years', '50-59 Years', '60 years or above'],
        datasets: [
            {
                label: 'Age Groups',
                data: [screeningDetails?.age_groups?.age_18_29, screeningDetails?.age_groups?.age_30_39, screeningDetails?.age_groups?.age_40_49, screeningDetails?.age_groups?.age_50_59, screeningDetails?.age_groups?.age_60_above],
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                ],
                borderWidth: 1,
            },
        ],
    };

    const updateValue = (key, value) => setFilterData((prevState) => ({ ...prevState, [key]: value }));

    const labels = summaryData.map(item => item.disease);

    const knownCasesData = summaryData.map(item => {
        const knownMale = Number(screeningDetails?.[item.key]?.known_male) || 0;
        const knownFemale = Number(screeningDetails?.[item.key]?.known_female) || 0;
        return (knownMale + knownFemale) || null;
    });

    const suspectedCasesData = summaryData.map(item => {
        const suspectedMale = Number(screeningDetails?.[item.key]?.suspected_male) || 0;
        const suspectedFemale = Number(screeningDetails?.[item.key]?.suspected_female) || 0;
        return (suspectedMale + suspectedFemale) || null;
    });

    const referredCasesData = summaryData.map(item => {
        const referredMale = Number(screeningDetails?.[item.key]?.referred_male) || 0;
        const referredFemale = Number(screeningDetails?.[item.key]?.referred_female) || 0;
        return (referredMale + referredFemale) || null;
    });

    console.log(referredCasesData, suspectedCasesData, knownCasesData, "knownCasesData")

    // Chart.js data config
    const data = {
        labels,
        datasets: [
            {
                label: 'Known Cases',
                data: knownCasesData,
                backgroundColor: 'rgba(75, 192, 192, 0.8)',
                borderWidth: 1,
                minBarLength: 35,
            },
            {
                label: 'Suspected Cases',
                data: suspectedCasesData,
                backgroundColor: 'rgba(255, 206, 86, 0.8)',
                borderWidth: 1,
                minBarLength: 35,
            },
            {
                label: 'Referred Cases',
                data: referredCasesData,
                backgroundColor: 'rgba(255, 99, 132, 0.8)',
                borderWidth: 1,
                minBarLength: 35,
            },
        ],
    };

    // Bar options config
    const barOptions = {
        responsive: true,
        plugins: {
            legend: { display: true },
            title: { display: false },
            datalabels: {
                anchor: 'end',
                align: 'start',
                rotation: -45,
                color: '#333',
                font: { weight: 'bold' },
                offset: -4,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 160000, // Set max height to balance BMI bar
                ticks: {
                    stepSize: 50000, // Increase Y-axis interval
                    color: '#000',
                    font: { size: 14, weight: 'bold' },
                },
                title: {
                    display: true,
                    text: 'Count',
                    color: '#000',
                    font: { size: 16, weight: 'bold' },
                },
            },
            x: {
                ticks: {
                    color: '#000',
                    font: { size: 14, weight: 'bold' },
                },
                title: {
                    display: true,
                    text: 'Cases by Disease Type and Status',
                    color: '#000',
                    font: { size: 16, weight: 'bold' },
                },
            },
        },
    };

    if (isLoading) {
        return (<div style={{ width: '98vw', height: '95vh', position: 'absolute', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
            <img style={{ width: 50, height: 50 }} src='./loading.gif' alt='loading' />
        </div>)
    }

    return (
        <div className="role-container">
            <CustomHeader
                heading={`Screening Summary (${headingName})`}
                showFilter
                onFilterToggle={() => setShowFilter((prevState => !prevState))}
                extraComp={
                    <>
                        {filterData?.from_date && filterData?.to_date &&
                            <div style={{ color: '#000', fontSize: 15, marginRight: 10, fontWeight: 'bold' }}>
                                {`Filter Range: ${moment(filterData?.from_date, 'YYYY-MM-DD').format('DD MMM YYYY')} - ${moment(filterData?.to_date, 'YYYY-MM-DD').format('DD MMM YYYY')}`}
                            </div>
                        }
                    </>
                }
                viewText={viewType === 'list' ? 'View in Bar' : 'View in List'}
                onViewToggle={() => {
                    if (viewType === 'list') {
                        setViewType('bar')
                    } else {
                        setViewType('list')
                    }
                }}
            />
            {/* Main Screening Table */}
            <div style={{ marginTop: 50 }} className="fm-item-container">
                <table className="fm-item-table">
                    <thead>
                        <tr>
                            {headers.map((header, idx) => (
                                <th key={idx} style={{ backgroundColor: '#217cc070', maxWidth: '200px', whiteSpace: 'break-spaces' }}>
                                    {header?.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {headers?.map((item, idx) => {
                                if (item?.key === 'health_facility_count') {
                                    return <td key={idx}>{healthFacilityCount}</td>
                                }
                                if (item?.key === 'village_count') {
                                    return <td key={idx}>{villageCount}</td>
                                }
                                return <td key={idx}>{screeningDetails?.[item?.key] || ''}</td>
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Summary Section */}

            {viewType === 'list' &&
                <div style={{ marginTop: 20 }} className="summary-table-container">
                    <table className="summary-table">
                        <thead>
                            <tr>
                                <th>DISEASE TYPE</th>
                                <th>Total Known Cases</th>
                                <th>% of Known Cases</th>
                                <th>Total Suspected Cases</th>
                                <th>% of Suspected Cases</th>
                                <th>Total Referred Cases</th>
                                <th>% of Referred Cases</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaryData.map((row, idx) => {
                                const knownMale = Number(screeningDetails?.[row?.key]?.known_male) || 0;
                                const knownFemale = Number(screeningDetails?.[row?.key]?.known_female) || 0;
                                const suspectedMale = Number(screeningDetails?.[row?.key]?.suspected_male) || 0;
                                const suspectedFemale = Number(screeningDetails?.[row?.key]?.suspected_female) || 0;
                                const referredMale = Number(screeningDetails?.[row?.key]?.referred_male) || 0;
                                const referredFemale = Number(screeningDetails?.[row?.key]?.referred_female) || 0;

                                const totalKnown = knownMale + knownFemale;
                                const totalSuspected = suspectedMale + suspectedFemale;
                                const totalReferred = referredMale + referredFemale;

                                const knownPercentage = ((totalKnown / 489155) * 100).toFixed(2);
                                const suspectedPercentage = ((totalSuspected / 489155) * 100).toFixed(2);
                                const referredPercentage = ((totalReferred / 489155) * 100).toFixed(2);

                                return (
                                    <tr key={idx}>
                                        <td style={{ color: '#000', fontSize: 15, fontWeight: 'bold', background: 'rgba(33, 124, 192, 0.44)' }}>{row.disease}</td>
                                        <td>{totalKnown}</td>
                                        <td>{knownPercentage}%</td>
                                        <td>{totalSuspected}</td>
                                        <td>{suspectedPercentage}%</td>
                                        <td>{totalReferred}</td>
                                        <td>{referredPercentage}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            }

            {viewType === 'bar' &&
                <div style={{ marginTop: 20 }}>
                    <Bar data={data} options={barOptions} />
                </div>
            }

            {/* <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '50px', marginTop: '50px' }}>
                <div style={{ textAlign: 'center' }}>
                    <h4>M/F Population</h4>
                    <div style={{ width: '300px', height: '300px' }}>
                        <Pie data={genderData} options={options} />
                    </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                    <h4>Age Groups</h4>
                    <div style={{ width: '300px', height: '300px' }}>
                        <Pie data={ageGroupsData} options={options} />
                    </div>
                </div>
            </div> */}
            {showFilter &&
                <DateFilterModal
                    visible={showFilter}
                    onDismiss={() => setShowFilter((prevState => !prevState))}
                    resetFilters={() => {
                        setShowFilter((prevState => !prevState))
                        setFilterData(null);
                        fetchData({});
                    }}
                    filterData={filterData}
                    applyFilters={() => {
                        setShowFilter((prevState => !prevState))
                        fetchData(filterData);
                    }}
                    updateValue={updateValue}
                />
            }
        </div>
    );
};

export default StateWiseScreening;
